import { Trans, useLingui } from '@lingui/react/macro'
import { LineChart } from '@mantine/charts'
import '@mantine/charts/styles.css'
import {
  Card,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Text,
} from '@mantine/core'
import type { MonthlyDataPoint } from './consumptionCalculations'
import { TYPE_CONFIG } from './utils'

type ConsumptionChartProps = {
  selectedType: 'electricity' | 'water' | 'heating'
  selectedYear: number
  monthlyData: MonthlyDataPoint[]
  onTypeChange: (value: 'electricity' | 'water' | 'heating') => void
  onYearChange: (value: number) => void
  availableYears: Array<{ value: string; label: string }>
  unit: string
}

export function ConsumptionChart({
  selectedType,
  selectedYear,
  monthlyData,
  onTypeChange,
  onYearChange,
  availableYears,
  unit,
}: ConsumptionChartProps) {
  const { t } = useLingui()
  return (
    <>
      {/* Chart Controls */}
      <Card withBorder radius="lg" p="md">
        <Group justify="space-between" wrap="wrap">
          <SegmentedControl
            value={selectedType}
            onChange={(value) =>
              onTypeChange(value as 'electricity' | 'water' | 'heating')
            }
            data={[
              { value: 'electricity', label: t`Electricity` },
              { value: 'water', label: t`Water` },
              { value: 'heating', label: t`Heating` },
            ]}
          />
          <Select
            value={String(selectedYear)}
            onChange={(value) => onYearChange(Number(value))}
            data={availableYears}
            w={120}
          />
        </Group>
      </Card>

      {/* Monthly Chart */}
      <Card withBorder radius="lg" p="md">
        <Stack gap="md">
          <Group justify="space-between">
            <Text fw={600} size="sm">
              <Trans>Monthly consumption</Trans>
            </Text>
            <Text size="xs" c="dimmed">
              <Trans>Comparison to previous year</Trans>
            </Text>
          </Group>

          <LineChart
            h={300}
            w="100%"
            data={monthlyData}
            dataKey="month"
            series={[
              {
                name: 'current',
                label: `${selectedYear}`,
                color: TYPE_CONFIG[selectedType].color,
              },
              {
                name: 'previous',
                label: `${selectedYear - 1}`,
                color: 'gray.5',
                strokeDasharray: '5 5',
              },
            ]}
            curveType="linear"
            connectNulls
            withLegend
            legendProps={{ verticalAlign: 'bottom', height: 50 }}
            gridAxis="y"
            unit={` ${unit}`}
            valueFormatter={(value) => value.toFixed(1)}
          />
        </Stack>
      </Card>
    </>
  )
}
